/**
 * @fileoverview ObjectEditModal
 * @description Modal para editar un objeto (incluyendo subir foto).
 *              Al guardar, comprime la imagen si es necesaria y la sube a /uploadFoto
 */

import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import DynamicForm from "../../components/forms/DynamicForm";
import { getFieldsForUserEdicion } from "../helpers/recordFieldsHelper";
import { actualizarObjeto } from "../../services/api";
import UpgradeModal from "../../components/UpgradeModal";
import FotoIDModal from "./FotoIDModal";

export default function ObjectEditModal({ objeto, usuario, onClose, onSubmit }) {
  const fieldsForEdicionObjeto = getFieldsForUserEdicion(objeto);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [comprimiendoFoto, setComprimiendoFoto] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Estado para el banner de upgrade
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Estado para Foto-ID (mascotas)
  const [showFotoIDModal, setShowFotoIDModal] = useState(false);
  const [fotoIDCompleto, setFotoIDCompleto] = useState(!!objeto?.fotoID?.completo);

  // Estado para idioma de recuperación (Premium)
  const [idiomaRecuperacion, setIdiomaRecuperacion] = useState(objeto?.idiomaRecuperacion || 'es');

  // Estado para esterilización (solo mascotas)
  const [esterilizado, setEsterilizado] = useState(!!objeto?.esterilizado);
  const [fechaEsterilizacion, setFechaEsterilizacion] = useState(objeto?.fechaEsterilizacion || '');

  // Verificar si mostrar el banner de upgrade
  useEffect(() => {
    if (objeto?.plan === 'light') {
      const dismissedKey = `upgrade_banner_dismissed_${objeto.id}`;
      const isDismissed = localStorage.getItem(dismissedKey);
      if (!isDismissed) {
        setShowUpgradeBanner(true);
      }
    }
  }, [objeto?.id, objeto?.plan]);

  const handleDismissBanner = (e) => {
    e.stopPropagation();
    const dismissedKey = `upgrade_banner_dismissed_${objeto.id}`;
    localStorage.setItem(dismissedKey, 'true');
    setShowUpgradeBanner(false);
  };

  const handleOpenUpgradeModal = () => {
    setShowUpgradeModal(true);
  };

  const safeRecordData = {
    ...objeto,
    fotoFile: undefined,
  };

  /**
   * Comprime la imagen si es necesario
   */
  async function comprimirImagenSiEsNecesario(file) {
    // Solo comprimir si es imagen y pesa más de 500KB
    if (!file.type.startsWith('image/') || file.size <= 500 * 1024) {
      return file;
    }

    const options = {
      maxSizeMB: 1.5, // Máximo 1.5MB (margen de seguridad bajo el límite de 2MB)
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/webp' // Mejor compresión
    };

    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.warn("[ObjectEditModal] Error al comprimir, usando original:", error);
      return file;
    }
  }

  /**
   * Sube la foto a /uploadFoto
   * @returns {Object} { path, updatedAt } - Path en Storage y timestamp para cache-busting
   */
  async function subirFotoAlServidor(file, ownerId, objetoId) {
    const formData = new FormData();
    formData.append("ownerId", ownerId);
    formData.append("objetoId", objetoId);
    formData.append("foto", file);

    const resp = await fetch("/uploadFoto", {
      method: "POST",
      body: formData,
    });

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({ error: "Error desconocido del servidor" }));
      throw new Error(errorData.error || `Error ${resp.status}: No se pudo subir la foto`);
    }

    const data = await resp.json();

    if (data.error) {
      throw new Error(data.error);
    }

    // Retornar path y updatedAt del servidor para cache-busting sincronizado
    return {
      path: data.path || data.url,
      updatedAt: data.updatedAt || new Date().toISOString()
    };
  }

  /**
   * handleSubmit => Maneja compresión, subida de foto y actualización
   */
  async function handleSubmit(formState) {
    setGuardando(true);

    try {
      const file = formState.fotoFile;
      // Mantener valores existentes si no hay nueva foto - usar undefined (no string vacío)
      // para que el backend no los incluya en la actualización y preserve los originales
      let fotoPath = objeto.fotoPath || undefined;
      let fotoUrl = objeto.fotoUrl || undefined;
      // ✅ Usar updatedAt del servidor para cache-busting sincronizado
      let serverUpdatedAt = null;

      if (file && file instanceof File) {
        // Comprimir si es necesario
        setComprimiendoFoto(true);
        const fileToUpload = await comprimirImagenSiEsNecesario(file);
        setComprimiendoFoto(false);

        // Subir
        setSubiendoFoto(true);
        try {
          const uploadResult = await subirFotoAlServidor(
            fileToUpload,
            objeto?.ownerId || "",
            objeto.id
          );
          fotoPath = uploadResult.path;
          serverUpdatedAt = uploadResult.updatedAt;
          // Construir URL del proxy con timestamp del servidor para cache-busting sincronizado
          // Esto asegura que el timestamp coincida con Firestore
          const cacheTimestamp = new Date(serverUpdatedAt).getTime();
          fotoUrl = `/api/archivo/objeto/${objeto.id}/foto?v=${cacheTimestamp}`;
        } catch (uploadError) {
          throw new Error(`Error al subir la foto: ${uploadError.message}`);
        } finally {
          setSubiendoFoto(false);
        }
      }

      // Actualizar objeto en Firestore
      // Solo incluir fotoPath/fotoUrl si tienen valor (para no sobrescribir con undefined)
      // Usar updatedAt del servidor si hubo upload, sino generar uno nuevo
      const updatedData = {
        ...objeto,
        ...formState,
        fotoFile: undefined,
        // Solo incluir foto fields si tienen valor, para preservar los existentes
        ...(fotoPath && { fotoPath }),
        ...(fotoUrl && { fotoUrl }),
        // Incluir idioma de recuperación si aplica (Premium no mascota)
        ...(objeto?.plan !== 'light' && objeto?.tipo !== 'mascota' && { idiomaRecuperacion }),
        // Incluir esterilización si es mascota
        ...(objeto?.tipo === 'mascota' && {
          esterilizado,
          fechaEsterilizacion: esterilizado ? (fechaEsterilizacion || null) : null
        }),
        // ✅ Usar updatedAt del servidor (si hubo upload) para cache-busting sincronizado
        updatedAt: serverUpdatedAt || new Date().toISOString(),
      };

      const resp = await actualizarObjeto(objeto.id, updatedData);

      if (resp.error) {
        throw new Error(`Error al actualizar: ${resp.error}`);
      }

      // ✅ SOLO UN toast aquí
      toast.success("Objeto actualizado correctamente", {
        autoClose: 2000,
        hideProgressBar: false
      });

      // ✅ Cerrar primero
      onClose();

      // ✅ Luego notificar al padre con datos actualizados (incluye fotoUrl)
      if (onSubmit) {
        onSubmit(updatedData);
      }
      
    } catch (err) {
      console.error("[ObjectEditModal] Error general:", err);
      toast.error(`No se pudo guardar el objeto: ${err.message}`);
    } finally {
      setGuardando(false);
      setComprimiendoFoto(false);
      setSubiendoFoto(false);
    }
  }

  // Determinar el tipo de objeto para el título e icono
  const tipoObjeto = objeto?.tipo || 'objeto';
  const getTituloYIcono = () => {
    switch(tipoObjeto) {
      case 'mascota':
        return { titulo: 'Editar Mascota', icono: 'bi-heart-fill' };
      case 'llavero':
        return { titulo: 'Editar Llavero', icono: 'bi-key-fill' };
      case 'persona':
        return { titulo: 'Editar Persona', icono: 'bi-person-fill' };
      case 'coleccionable':
        return { titulo: 'Editar Coleccionable', icono: 'bi-award-fill' };
      default:
        return { titulo: 'Editar Objeto', icono: 'bi-pencil-square' };
    }
  };

  // ✅ UNIVERSAL: Verificar si tiene datos de autenticidad (para TODOS los tipos)
  const esColeccionable = tipoObjeto === 'coleccionable';
  const tieneAutenticidad = (
    objeto?.numeroSerie ||
    objeto?.creadoPara
  );

  // Estado para controlar si la sección está expandida
  const [autenticidadExpandida, setAutenticidadExpandida] = useState(false);

  const { titulo, icono } = getTituloYIcono();

  // ══════════════════════════════════════════════════════════
  // PROGRESO DEL PERFIL (solo mascotas)
  // ══════════════════════════════════════════════════════════
  const calcularProgreso = (mascota) => {
    let progreso = 0;
    if (mascota.nombre && mascota.especie && mascota.fotoUrl) progreso += 40;
    const camposExt = [
      mascota.raza,
      mascota.edad || mascota.fechaNacimiento,
      mascota.esterilizado !== undefined && mascota.esterilizado !== '',
      mascota.alergias,
      mascota.comportamientoConExtranos
    ].filter(Boolean).length;
    progreso += Math.round((camposExt / 5) * 30);
    if (mascota.fotoID?.completo === true) progreso += 30;
    return Math.min(100, progreso);
  };

  const progresoPerfil = tipoObjeto === 'mascota' ? calcularProgreso(objeto) : null;

  const getItemsFaltantes = () => {
    if (tipoObjeto !== 'mascota') return [];
    const faltantes = [];
    if (!objeto.nombre) faltantes.push({ label: 'Nombre', key: 'nombre' });
    if (!objeto.especie) faltantes.push({ label: 'Especie', key: 'especie' });
    if (!objeto.fotoUrl) faltantes.push({ label: 'Foto', key: 'foto' });
    if (!objeto.raza) faltantes.push({ label: 'Raza', key: 'raza' });
    if (!objeto.fechaNacimiento && !objeto.edad) faltantes.push({ label: 'Fecha de nacimiento', key: 'fechaNacimiento' });
    if (!objeto.alergias) faltantes.push({ label: 'Alergias', key: 'alergias' });
    if (!objeto.comportamientoConExtranos) faltantes.push({ label: 'Comportamiento', key: 'comportamiento' });
    if (!fotoIDCompleto) faltantes.push({ label: 'Foto-ID', key: 'fotoID', isFotoID: true });
    return faltantes;
  };

  const itemsFaltantes = tipoObjeto === 'mascota' ? getItemsFaltantes() : [];

  // ══════════════════════════════════════════════════════════
  // SECCIONES DE CAMPOS (solo mascotas)
  // ══════════════════════════════════════════════════════════
  const fieldsWithSections = (() => {
    if (tipoObjeto !== 'mascota') return fieldsForEdicionObjeto;
    const basicKeys = ['nombre', 'fotoFile', 'especie'];
    const result = [];
    let addedBasic = false;
    let addedExtended = false;
    fieldsForEdicionObjeto.forEach((field) => {
      if (!addedBasic && basicKeys.includes(field.fieldKey)) {
        result.push({ fieldKey: '_section_basico', inputType: 'sectionHeader', label: 'Datos básicos' });
        addedBasic = true;
      }
      if (!addedExtended && addedBasic && !basicKeys.includes(field.fieldKey)) {
        result.push({
          fieldKey: '_section_extendido',
          inputType: 'sectionHeader',
          label: 'Datos extendidos (opcional)',
          helperText: 'Estos datos ayudan a identificar mejor a tu mascota'
        });
        addedExtended = true;
      }
      result.push(field);
    });
    return result;
  })();

  return (
    <div
      className="modal show"
      style={{ 
        display: "block", 
        background: "rgba(0,0,0,0.5)",
        zIndex: 'var(--z-modal)'
      }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" 
             style={{borderRadius: 'var(--radius-xl)'}}>
          
          {/* Header con gradiente feature */}
          <div className="modal-header modal-header-gradient">
            <h5 className="modal-title"
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--white)'
                }}>
              <i className={`bi ${icono} me-2`}></i>
              {titulo}
            </h5>
            <button type="button" 
                    className="btn-close btn-close-white" 
                    onClick={onClose}>
            </button>
          </div>

          {/* Body */}
          <div className="modal-body"
               style={{padding: 'var(--space-5)'}}>

            {/* Progreso del perfil - solo mascotas */}
            {tipoObjeto === 'mascota' && progresoPerfil !== null && (
              <div style={{
                marginBottom: 'var(--space-4)',
                padding: 'var(--space-3)',
                background: 'var(--gray-50)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--gray-200)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-2)'
                }}>
                  <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--gray-700)'
                  }}>
                    Perfil completado
                  </span>
                  <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: progresoPerfil === 100 ? '#16a34a' : 'var(--gray-600)'
                  }}>
                    {progresoPerfil}%
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  background: 'var(--gray-200)',
                  borderRadius: 'var(--radius-full)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${progresoPerfil}%`,
                    background: progresoPerfil === 100
                      ? 'linear-gradient(90deg, #16a34a, #22c55e)'
                      : 'linear-gradient(90deg, #84cc16, #a3e635)',
                    borderRadius: 'var(--radius-full)',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
                {itemsFaltantes.length > 0 && (
                  <div style={{ marginTop: 'var(--space-2)' }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-500)',
                      marginBottom: '4px'
                    }}>
                      Falta:
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {itemsFaltantes.map((item) => (
                        item.isFotoID ? (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => setShowFotoIDModal(true)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              fontSize: 'var(--font-size-xs)',
                              background: '#FEF3C7',
                              border: '1px solid #FDE68A',
                              borderRadius: 'var(--radius-full)',
                              color: '#92400E',
                              cursor: 'pointer',
                              fontWeight: 'var(--font-weight-medium)'
                            }}
                          >
                            📸 {item.label}
                          </button>
                        ) : (
                          <span
                            key={item.key}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 8px',
                              fontSize: 'var(--font-size-xs)',
                              background: 'var(--gray-100)',
                              borderRadius: 'var(--radius-full)',
                              color: 'var(--gray-600)'
                            }}
                          >
                            {item.label}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Banner de Upgrade para plan Light */}
            {showUpgradeBanner && (
              <div
                onClick={handleOpenUpgradeModal}
                style={{
                  background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
                  border: '1px solid #A7F3D0',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-2) var(--space-3)',
                  marginBottom: 'var(--space-4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)';
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}>
                  <span style={{ fontSize: '16px' }}>⭐</span>
                  <span style={{
                    fontSize: 'var(--font-size-sm)',
                    color: '#047857',
                    fontWeight: '500'
                  }}>
                    Sube tu plan y obtén mas beneficios
                  </span>
                </div>
                <button
                  onClick={handleDismissBanner}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    color: '#6B7280',
                    fontSize: '16px',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Cerrar"
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
            )}

            {/* Selector de Idioma de Recuperación - Solo para objetos EXISTENTES, Premium y no mascota */}
            {objeto?.id && objeto?.plan !== 'light' && objeto?.tipo !== 'mascota' && (
              <div style={{
                background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                border: '1px solid #93C5FD',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-2)'
                }}>
                  <i className="bi bi-globe2" style={{
                    color: '#2563EB',
                    fontSize: '18px'
                  }}></i>
                  <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: '#1E40AF'
                  }}>
                    Idioma de la página de recuperación
                  </span>
                  <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: '#3B82F6',
                    background: '#DBEAFE',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                    marginLeft: 'auto'
                  }}>
                    Premium
                  </span>
                </div>

                <select
                  value={idiomaRecuperacion}
                  onChange={(e) => setIdiomaRecuperacion(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'var(--space-2) var(--space-3)',
                    fontSize: 'var(--font-size-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '2px solid #93C5FD',
                    background: 'white',
                    color: '#1F2937',
                    cursor: 'pointer'
                  }}
                >
                  <option value="es">🇲🇽 Español</option>
                  <option value="en">🇺🇸 English (para viajes)</option>
                </select>

                <p style={{
                  fontSize: 'var(--font-size-xs)',
                  color: '#1E40AF',
                  marginTop: 'var(--space-2)',
                  marginBottom: 0,
                  lineHeight: 'var(--line-height-relaxed)'
                }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Si pierdes este objeto en el extranjero, quien lo encuentre verá la información en el idioma seleccionado.
                </p>
              </div>
            )}

            {/* Estados de carga con mejor diseño */}
            {comprimiendoFoto && (
              <div className="alert alert-info d-flex align-items-center mb-4 rounded-lg"
                   style={{
                     fontSize: 'var(--font-size-sm)',
                     padding: 'var(--space-3) var(--space-4)',
                     borderRadius: 'var(--radius-md)'
                   }}>
                <div className="spinner-border spinner-border-sm me-3" 
                     role="status"
                     style={{
                       width: '1.5rem',
                       height: '1.5rem'
                     }}>
                </div>
                <div>
                  <strong>Optimizando imagen...</strong>
                  <div style={{fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)'}}>
                    Comprimiendo para mejor rendimiento
                  </div>
                </div>
              </div>
            )}
            
            {subiendoFoto && (
              <div className="alert alert-primary d-flex align-items-center mb-4 rounded-lg"
                   style={{
                     fontSize: 'var(--font-size-sm)',
                     padding: 'var(--space-3) var(--space-4)',
                     borderRadius: 'var(--radius-md)'
                   }}>
                <div className="spinner-border spinner-border-sm me-3" 
                     role="status"
                     style={{
                       width: '1.5rem',
                       height: '1.5rem'
                     }}>
                </div>
                <div>
                  <strong>Subiendo foto...</strong>
                  <div style={{fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)'}}>
                    Por favor espera un momento
                  </div>
                </div>
              </div>
            )}
            
            {guardando && !comprimiendoFoto && !subiendoFoto && (
              <div className="alert alert-success d-flex align-items-center mb-4 rounded-lg"
                   style={{
                     fontSize: 'var(--font-size-sm)',
                     padding: 'var(--space-3) var(--space-4)',
                     borderRadius: 'var(--radius-md)'
                   }}>
                <div className="spinner-border spinner-border-sm me-3" 
                     role="status"
                     style={{
                       width: '1.5rem',
                       height: '1.5rem'
                     }}>
                </div>
                <div>
                  <strong>Guardando cambios...</strong>
                  <div style={{fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)'}}>
                    Finalizando actualización
                  </div>
                </div>
              </div>
            )}

            {/* Formulario */}
            <DynamicForm
              fields={fieldsWithSections}
              view="userEdicion"
              recordData={safeRecordData}
              onSubmit={handleSubmit}
              onCancel={onClose}
            />

            {/* Sección de Esterilización (solo mascotas) */}
            {tipoObjeto === 'mascota' && (
              <div style={{
                marginTop: 'var(--space-4)',
                padding: 'var(--space-3)',
                background: 'var(--gray-50)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--gray-200)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-2)'
                }}>
                  <i className="bi bi-shield-plus" style={{
                    color: '#84cc16',
                    fontSize: '18px'
                  }}></i>
                  <span style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--gray-800)'
                  }}>
                    Esterilización
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-2) 0'
                }}>
                  <label
                    htmlFor="esterilizado-toggle"
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-700)',
                      cursor: 'pointer'
                    }}
                  >
                    ¿Está esterilizado/a?
                  </label>
                  <button
                    id="esterilizado-toggle"
                    type="button"
                    role="switch"
                    aria-checked={esterilizado}
                    onClick={() => setEsterilizado(!esterilizado)}
                    style={{
                      width: '44px',
                      height: '24px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: esterilizado ? '#84cc16' : '#d1d5db',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      flexShrink: 0
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      top: '2px',
                      left: esterilizado ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left 0.2s ease'
                    }}></span>
                  </button>
                </div>

                {esterilizado && (
                  <div style={{ marginTop: 'var(--space-2)' }}>
                    <label style={{
                      display: 'block',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: '4px'
                    }}>
                      Fecha de esterilización (opcional)
                    </label>
                    <input
                      type="date"
                      value={fechaEsterilizacion}
                      onChange={(e) => setFechaEsterilizacion(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      style={{
                        width: '100%',
                        padding: 'var(--space-2) var(--space-3)',
                        fontSize: 'var(--font-size-sm)',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--gray-300)',
                        background: 'white',
                        color: 'var(--gray-800)'
                      }}
                    />
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-500)',
                      marginTop: '4px',
                      marginBottom: 0
                    }}>
                      Si no conoces la fecha exacta, puedes dejar este campo vacío.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ✅ Sección de Autenticidad COLAPSABLE (solo lectura) - UNIVERSAL para TODOS los tipos */}
            {tieneAutenticidad && (
              <div style={{
                marginTop: 'var(--space-4)',
                background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)',
                borderRadius: 'var(--radius-lg)',
                border: '2px solid #DDD6FE',
                overflow: 'hidden'
              }}>
                {/* Header clickeable para expandir/colapsar */}
                <button
                  type="button"
                  onClick={() => setAutenticidadExpandida(!autenticidadExpandida)}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <i className="bi bi-shield-check" style={{
                    color: '#8B5CF6',
                    fontSize: '18px'
                  }}></i>
                  <span style={{
                    flex: 1,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: '#5B21B6'
                  }}>
                    Datos de Autenticidad
                  </span>
                  <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: '#7C3AED',
                    background: '#EDE9FE',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                    marginRight: 'var(--space-2)'
                  }}>
                    Solo lectura
                  </span>
                  <i className={`bi bi-chevron-${autenticidadExpandida ? 'up' : 'down'}`} style={{
                    color: '#7C3AED',
                    fontSize: '14px',
                    transition: 'transform 0.2s ease'
                  }}></i>
                </button>

                {/* Contenido expandible */}
                {autenticidadExpandida && (
                  <div style={{ padding: '0 var(--space-3) var(--space-3)' }}>
                    {/* Campos de solo lectura */}
                    <div style={{
                      display: 'grid',
                      gap: 'var(--space-3)'
                    }}>
                      {/* Número de Serie - UNIVERSAL */}
                      {objeto?.numeroSerie && (
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-xs)',
                            color: '#6B7280',
                            marginBottom: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            Número de Serie
                          </label>
                          <div style={{
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'white',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid #DDD6FE',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: '#1F2937',
                            fontFamily: 'monospace'
                          }}>
                            {objeto.numeroSerie}
                          </div>
                        </div>
                      )}

                      {/* Creado Para - UNIVERSAL */}
                      {objeto?.creadoPara && (
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-xs)',
                            color: '#6B7280',
                            marginBottom: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            Creado Para
                          </label>
                          <div style={{
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                            borderRadius: 'var(--radius-md)',
                            border: '2px solid #FDE047',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-bold)',
                            color: '#78350F',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                          }}>
                            <i className="bi bi-star-fill" style={{ color: '#F59E0B' }}></i>
                            {objeto.creadoPara}
                          </div>
                        </div>
                      )}

                      {/* === CAMPOS ADICIONALES SOLO PARA COLECCIONABLES === */}
                      {esColeccionable && (
                        <>
                          {/* Tipo de Producto */}
                          {objeto?.autenticidad?.tipoProducto && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                color: '#6B7280',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Tipo de Producto
                              </label>
                              <div style={{
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'white',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid #DDD6FE',
                                fontSize: 'var(--font-size-sm)',
                                color: '#1F2937'
                              }}>
                                {objeto.autenticidad.tipoProducto}
                              </div>
                            </div>
                          )}

                          {/* Edición */}
                          {objeto?.autenticidad?.edicion && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                color: '#6B7280',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Edición
                              </label>
                              <div style={{
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'white',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid #DDD6FE',
                                fontSize: 'var(--font-size-sm)',
                                color: '#1F2937'
                              }}>
                                {objeto.autenticidad.edicion}
                              </div>
                            </div>
                          )}

                          {/* Mensaje */}
                          {objeto?.autenticidad?.mensaje && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                color: '#6B7280',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Mensaje de Autenticidad
                              </label>
                              <div style={{
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'white',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid #DDD6FE',
                                fontSize: 'var(--font-size-sm)',
                                color: '#1F2937',
                                fontStyle: 'italic'
                              }}>
                                "{objeto.autenticidad.mensaje}"
                              </div>
                            </div>
                          )}

                          {/* Fecha de Fabricación */}
                          {objeto?.autenticidad?.fechaFabricacion && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                color: '#6B7280',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Fecha de Fabricación
                              </label>
                              <div style={{
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'white',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid #DDD6FE',
                                fontSize: 'var(--font-size-sm)',
                                color: '#1F2937'
                              }}>
                                {objeto.autenticidad.fechaFabricacion}
                              </div>
                            </div>
                          )}

                          {/* Campos Personalizados */}
                          {objeto?.autenticidad?.campoPersonalizado1 && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                color: '#6B7280',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Información Adicional
                              </label>
                              <div style={{
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'white',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid #DDD6FE',
                                fontSize: 'var(--font-size-sm)',
                                color: '#1F2937'
                              }}>
                                {objeto.autenticidad.campoPersonalizado1}
                              </div>
                            </div>
                          )}

                          {objeto?.autenticidad?.campoPersonalizado2 && (
                            <div>
                              <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                color: '#6B7280',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Detalle Adicional
                              </label>
                              <div style={{
                                padding: 'var(--space-2) var(--space-3)',
                                background: 'white',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid #DDD6FE',
                                fontSize: 'var(--font-size-sm)',
                                color: '#1F2937'
                              }}>
                                {objeto.autenticidad.campoPersonalizado2}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Nota informativa */}
                    <div style={{
                      marginTop: 'var(--space-3)',
                      padding: 'var(--space-2)',
                      background: 'rgba(139, 92, 246, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)'
                    }}>
                      <i className="bi bi-info-circle" style={{
                        color: '#7C3AED',
                        fontSize: '14px'
                      }}></i>
                      <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: '#5B21B6'
                      }}>
                        Los datos de autenticidad no pueden modificarse para garantizar la integridad del certificado.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Upgrade */}
      {showUpgradeModal && (
        <UpgradeModal
          objeto={objeto}
          usuario={usuario}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      {/* Modal de Foto-ID (mascotas) */}
      {showFotoIDModal && tipoObjeto === 'mascota' && (
        <FotoIDModal
          mascota={objeto}
          onClose={() => setShowFotoIDModal(false)}
          onFotoIDUpdated={(fotoID) => setFotoIDCompleto(!!fotoID?.completo)}
        />
      )}
    </div>
  );
}